import { useState } from "react";

const BarChart = ({ candidatesCount }) => {
  const [tooltip, setTooltip] = useState(null);

  const jobezeValue = candidatesCount?.jobezePortal || 0;
  const associated = candidatesCount?.employerPortal?.associatedWithJob || 0;
  const notAssociated =
    candidatesCount?.employerPortal?.notAssociatedWithJob || 0;

  const employerTotal = associated + notAssociated;
  const MAX = Math.max(jobezeValue, employerTotal, 1);

  const generateTicks = (max) => {
    if (max === 0) return [0];
    const rawStep = max / 3;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step =
      rawStep / magnitude >= 5
        ? 5 * magnitude
        : rawStep / magnitude >= 2
          ? 2 * magnitude
          : magnitude;
    const ticks = [];
    for (let i = 0; i <= max; i += step * 2) {
      ticks.push(Math.round(i));
    }
    if (ticks[ticks.length - 1] < max) ticks.push(Math.round(max));
    return ticks;
  };

  const ticks = generateTicks(MAX);

  const CHART_DATA = [
    {
      id: 0,
      label: "Jobeze\nPortal",
      type: "single",
      bar: { value: jobezeValue, color: "#006CB7" },
      tooltip: { bg: "#FFF", lines: [`${jobezeValue}`, "Jobeze Portal"] },
    },
    {
      id: 1,
      label: "Employer\nPortal",
      type: "stacked",
      segments: [
        {
          value: associated,
          color: "#3F95EA",
          tooltipBg: "#FFF",
          tooltipLines: [`${associated}`, "Job Associated", "Employer Portal"],
        },
        {
          value: notAssociated,
          color: "#8CBFF2",
          tooltipBg: "#FFF",
          tooltipLines: [
            `${notAssociated}`,
            "Job Not Associated",
            "Employer Portal",
          ],
        },
      ],
    },
  ];

  const handleMouseMove = (e, lines, bg) => {
    const rect = e.currentTarget
      .closest("[data-barchart-root]")
      .getBoundingClientRect();
    setTooltip({
      lines,
      bg,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // MIN_PCT ensures even very small values are still visible as a bar
  const MIN_PCT = 2;
  const getPct = (value) => {
    if (value === 0) return 0;
    return Math.max((value / MAX) * 100, MIN_PCT);
  };

  return (
    <div
      data-barchart-root
      style={{
        background: "#fff",
        borderRadius: "12px",
        height: "100%",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* Global tooltip */}
      {tooltip && (
        <div
          style={{
            position: "absolute",
            top: tooltip.y + 14,
            left: tooltip.x,
            transform: "translateX(-50%)",
            background: tooltip.bg,
            color: "rgba(0,0,0,0.90)",
            borderRadius: "7px",
            padding: "6px 14px",
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 16px #0003",
            pointerEvents: "none",
            zIndex: 50,
            textAlign: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-7px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "7px solid transparent",
              borderRight: "7px solid transparent",
              borderBottom: `7px solid ${tooltip.bg}`,
            }}
          />
          {tooltip.lines.map((t, i) => (
            <div
              key={i}
              style={{
                fontWeight: i === 0 ? 700 : 500,
                fontSize: i === 0 ? "14px" : "11px",
              }}
            >
              {t}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Y-axis title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            fontSize: "12px",
            fontWeight: "700",
            color: "rgba(0, 0, 0, 0.90)",
            marginRight: "4px",
            letterSpacing: "0.5px",
            flexShrink: 0,
          }}
        >
          Portal Name
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Bars area */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "clamp(12px, 3vh, 32px)",
              marginBottom: "8px",
            }}
          >
            {CHART_DATA.map((row) => (
              <div
                key={row.id}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {/* Y label */}
                <div
                  style={{
                    width: "62px",
                    fontSize: "11px",
                    color: "#555",
                    textAlign: "right",
                    whiteSpace: "pre-line",
                    flexShrink: 0,
                  }}
                >
                  {row.label}
                </div>

                {/* Bar */}
                <div style={{ flex: 1, position: "relative" }}>
                  {row.type === "single" ? (
                    (() => {
                      const bar = row.bar;
                      const pct = getPct(bar.value); 
                      return (
                        <div
                          style={{
                            background: "#E8F1FA",
                            borderRadius: "5px",
                            height: "25px",
                            width: "100%",
                            cursor: "pointer",
                            position: "relative",
                            overflow: "visible",
                          }}
                        >
                          <div
                            style={{
                              background: bar.color,
                              borderRadius: "5px",
                              height: "100%",
                              width: `${pct}%`,
                              transition: "width 0.7s cubic-bezier(.4,0,.2,1)",
                            }}
                            onMouseMove={(e) =>
                              handleMouseMove(
                                e,
                                row.tooltip.lines,
                                row.tooltip.bg,
                              )
                            }
                            onMouseLeave={() => setTooltip(null)}
                          />
                        </div>
                      );
                    })()
                  ) : (
                    <div
                      style={{
                        background: "#E8F1FA",
                        borderRadius: "5px",
                        height: "25px",
                        width: "100%",
                        display: "flex",
                        overflow: "visible",
                        position: "relative",
                      }}
                    >
                      {row.segments.map((seg, segIdx) => {
                        const pct = getPct(seg.value);
                        const isFirst = segIdx === 0;
                        const isLast = segIdx === row.segments.length - 1;
                        return (
                          <div
                            key={segIdx}
                            style={{
                              width: `${pct}%`,
                              background: seg.color,
                              height: "100%",
                              borderRadius: isFirst
                                ? "5px 0 0 5px"
                                : isLast
                                  ? "0 5px 5px 0"
                                  : "0",
                              cursor: "pointer",
                              position: "relative",
                              transition: "width 0.7s cubic-bezier(.4,0,.2,1)",
                              flexShrink: 0,
                            }}
                            onMouseMove={(e) =>
                              handleMouseMove(
                                e,
                                seg.tooltipLines,
                                seg.tooltipBg,
                              )
                            }
                            onMouseLeave={() => setTooltip(null)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Dynamic X-axis ticks */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingLeft: "70px",
              flexShrink: 0,
            }}
          >
            {ticks.map((t) => (
              <span key={t} style={{ fontSize: "11px", color: "#aaa" }}>
                {t.toLocaleString()}
              </span>
            ))}
          </div>

          {/* X-axis label */}
          <div
            style={{
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "700",
              color: "rgba(0, 0, 0, 0.90)",
              paddingLeft: "62px",
              marginTop: "5px",
              flexShrink: 0,
            }}
          >
            Count
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarChart;
